import queryString from "query-string";

import { EndpointType } from "app/pages/app-intelligence/constants";
import { DefaultFetchService } from "app/services/fetchService";

import type {
    AppId,
    CategoryId,
    Date,
    DemographicsAppsRecord,
    DemographicsCategoriesRecord,
    IDemographicsAgeResponse,
    IDemographicsGenderResponse,
    IDemographicsServerResponse,
    IDemographicsWidgetReqParams,
    IDemographicsWidgetsData,
    OverTimeAgeRecord,
    OverTimeGenderRecord,
} from "./types";
import { Demographic } from "./types";

export const getAverageData = (
    appsData: DemographicsAppsRecord,
): {
    genderData: Record<AppId, IDemographicsGenderResponse>;
    ageData: Record<AppId, IDemographicsAgeResponse>;
} => {
    const genderData = {};
    const ageData = {};
    Object.keys(appsData).forEach((appId) => {
        genderData[appId] = appsData[appId].Average.gender;
        ageData[appId] = appsData[appId].Average.age;
    });
    return { genderData, ageData };
};

const getOverTimeDataForSpecificKey = (
    singleAppOvertimeData: Record<Date, Record<Demographic, IDemographicsGenderResponse | IDemographicsAgeResponse>>,
    key,
): Record<Date, IDemographicsGenderResponse | IDemographicsAgeResponse> => {
    return Object.keys(singleAppOvertimeData).reduce((res, date) => {
        return { ...res, [date]: singleAppOvertimeData[date][key] };
    }, {});
};

const getOverTimeData = (
    appsData: DemographicsAppsRecord,
): {
    overTimeGenderData: Record<AppId, OverTimeGenderRecord>;
    overTimeAgeData: Record<AppId, OverTimeAgeRecord>;
} => {
    const genderData = {};
    const ageData = {};
    Object.keys(appsData).forEach((appId) => {
        const overtimeData = appsData[appId].Overtime;
        genderData[appId] = getOverTimeDataForSpecificKey(overtimeData, Demographic.GENDER); //appsData[appId].Overtime.gender;
        ageData[appId] = getOverTimeDataForSpecificKey(overtimeData, Demographic.AGE);
    });
    return { overTimeGenderData: genderData, overTimeAgeData: ageData };
};

const getOverTimeDataForSpecificCategory = (
    singleCategoryOvertimeData: Record<
        Date,
        Record<Demographic, IDemographicsGenderResponse | IDemographicsAgeResponse>
    >,
    key,
): Record<Date, IDemographicsGenderResponse | IDemographicsAgeResponse> => {
    return Object.keys(singleCategoryOvertimeData).reduce((res, date) => {
        return { ...res, [date]: singleCategoryOvertimeData[date][key] };
    }, {});
};

const getOverTimeCategoriesData = (
    categoriesData: DemographicsCategoriesRecord,
): {
    overTimeGenderCategoriesData: Record<CategoryId, OverTimeGenderRecord>;
    overTimeAgeCategoriesData: Record<CategoryId, OverTimeAgeRecord>;
} => {
    const genderData = {};
    const ageData = {};
    Object.keys(categoriesData).forEach((category) => {
        const overtimeData = categoriesData[category];
        genderData[category] = getOverTimeDataForSpecificCategory(overtimeData, Demographic.GENDER);
        ageData[category] = getOverTimeDataForSpecificCategory(overtimeData, Demographic.AGE);
    });
    return { overTimeGenderCategoriesData: genderData, overTimeAgeCategoriesData: ageData };
};

export const getTransformedData = (
    rawData: IDemographicsServerResponse,
): Omit<IDemographicsWidgetsData, "requestParams"> => {
    const appsData = rawData.AppsDemographics;
    const categoriesData = rawData.CategoriesDemographics;
    const { genderData, ageData } = getAverageData(appsData);
    const { overTimeGenderData, overTimeAgeData } = getOverTimeData(appsData);
    const { overTimeGenderCategoriesData, overTimeAgeCategoriesData } = getOverTimeCategoriesData(categoriesData);

    return {
        averageData: { gender: genderData, age: ageData },
        overTimeData: { gender: overTimeGenderData, age: overTimeAgeData },
        overTimeCategoriesData: {
            gender: overTimeGenderCategoriesData,
            age: overTimeAgeCategoriesData,
        },
    };
};

const endpointBase = "api/AppDemographics/appsAndCategories";

const fetchService = DefaultFetchService.getInstance();

export const getEndpoint = (stateName: string, type: EndpointType | string, params?: IDemographicsWidgetReqParams) => {
    const endpointWithoutParams = `${endpointBase}/${type}`;

    if (params) {
        return `${endpointWithoutParams}?${queryString.stringify(params)}`;
    }

    return endpointWithoutParams;
};
export const getDemographicData = (genericReqParams, stateName: string): Promise<IDemographicsServerResponse> =>
    fetchService.get<IDemographicsServerResponse>(getEndpoint(stateName, EndpointType.DATA), genericReqParams);
